import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Shield, 
  ChevronRight,
  ChevronDown,
  CheckCircle,
  AlertTriangle,
  Info,
  X,
  XCircle,
  Clock,
  Eye,
  Plus,
  Calendar,
  Activity,
  Loader2
} from 'lucide-react';
import { apiClient, Cluster, RiskAssessment, TriggerRiskAssessmentRequest, cleanErrorMessage } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { ReactComponent as KubernetesIcon } from '../assets/icons/cluster.svg';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';


interface ClusterGroup {
  cluster_id: string;
  cluster_name: string;
  assessments: RiskAssessment[];
  isExpanded: boolean;
  latestAssessment?: RiskAssessment;
  ongoingCount: number;
  completedCount: number;
  failedCount: number;
}

export function KubernetesRiskAssessment() {
  const { isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const PAGE_SIZE = 20;
  
  const [assessments, setAssessments] = useState<RiskAssessment[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [clustersLoading, setClustersLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedClusterId, setSelectedClusterId] = useState<string>('all');
  const [, setNextPageToken] = useState('');
  const [pageHistory, setPageHistory] = useState<string[]>(['']);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [, setHasMorePages] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [showNewAssessmentDialog, setShowNewAssessmentDialog] = useState(false);
  const [selectedClusterForAssessment, setSelectedClusterForAssessment] = useState<string>('');
  const [isStartingAssessment, setIsStartingAssessment] = useState(false);
  const [assessmentModalError, setAssessmentModalError] = useState<string>('');
  
  const [clusterGroups, setClusterGroups] = useState<ClusterGroup[]>([]);



  // Initialize from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const clusterParam = urlParams.get('cluster');
    
    if (clusterParam) {
      setSelectedClusterId(clusterParam);
    }
  }, [location.search]);

  // Update URL when cluster changes
  const updateURLParams = (newCluster?: string) => {
    const params = new URLSearchParams(location.search);
    
    if (newCluster !== undefined) {
      if (newCluster === 'all') {
        params.delete('cluster');
      } else {
        params.set('cluster', newCluster);
      }
    }
    
    const paramString = params.toString();
    const newUrl = paramString ? `/risk-assessment?${paramString}` : '/risk-assessment';
    navigate(newUrl, { replace: true });
  };

  const handleClusterChange = (newCluster: string) => {
    setSelectedClusterId(newCluster);
    updateURLParams(newCluster);
  };

  // Load clusters on component mount
  useEffect(() => {
    loadClusters();
    loadAssessments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load assessments when cluster selection changes
  useEffect(() => {
    // Reset pagination when cluster changes
    setPageHistory(['']);
    setCurrentPageIndex(0);
    setNextPageToken('');
    setHasMorePages(false);
    loadAssessments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClusterId]);

  const loadClusters = async () => {
    setClustersLoading(true);
    try {
      const response = await apiClient.listClusters();
      setClusters(response.clusters || []);
      
      // Default to "All Clusters" - don't change selectedClusterId if it's already 'all'
      // This ensures the default behavior is to show all clusters unless explicitly changed
    } catch (err: any) {
      console.error('Error loading clusters:', err);
    } finally {
      setClustersLoading(false);
    }
  };

  const loadAssessments = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiClient.listRiskAssessments(
        selectedClusterId === 'all' ? undefined : selectedClusterId,
        PAGE_SIZE,
        pageHistory[currentPageIndex]
      );
      
      // Sort by status (ongoing first) then by creation date (newest first)
      const sortedAssessments = response.assessments.sort((a, b) => {
        // Prioritize ongoing assessments
        if (a.status === 'ongoing' && b.status !== 'ongoing') return -1;
        if (b.status === 'ongoing' && a.status !== 'ongoing') return 1;
        
        // Then sort by creation date (newest first)
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      
      setAssessments(sortedAssessments);
      setTotalCount(response.totalCount);
      setHasMorePages(!!response.nextPageToken);
      setNextPageToken(response.nextPageToken);
      
      // Group assessments by cluster if showing all clusters
      if (selectedClusterId === 'all') {
        groupAssessmentsByCluster(sortedAssessments);
      } else {
        setClusterGroups([]); // Clear grouping for specific cluster view
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load risk assessments');
      setAssessments([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const groupAssessmentsByCluster = (assessments: RiskAssessment[]) => {
    const groups = new Map<string, RiskAssessment[]>();
    
    assessments.forEach(assessment => {
      const clusterId = assessment.cluster_id;
      if (!groups.has(clusterId)) {
        groups.set(clusterId, []);
      }
      groups.get(clusterId)!.push(assessment);
    });

    const clusterGroups: ClusterGroup[] = Array.from(groups.entries())
      .map(([clusterId, assessments]) => {
        // Sort assessments within each cluster (ongoing first, then newest)
        assessments.sort((a, b) => {
          if (a.status === 'ongoing' && b.status !== 'ongoing') return -1;
          if (b.status === 'ongoing' && a.status !== 'ongoing') return 1;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });

        const latestAssessment = assessments[0];
        const ongoingCount = assessments.filter(a => a.status === 'ongoing').length;
        const completedCount = assessments.filter(a => a.status === 'completed').length;
        const failedCount = assessments.filter(a => a.status === 'failed').length;

        return {
          cluster_id: clusterId,
          cluster_name: assessments[0].cluster_name,
          assessments,
          isExpanded: ongoingCount > 0, // Auto-expand clusters with ongoing assessments
          latestAssessment,
          ongoingCount,
          completedCount,
          failedCount
        };
      })
      .sort((a, b) => {
        // Sort clusters by latest assessment date (newest first)
        const aLatest = a.latestAssessment ? new Date(a.latestAssessment.created_at).getTime() : 0;
        const bLatest = b.latestAssessment ? new Date(b.latestAssessment.created_at).getTime() : 0;
        return bLatest - aLatest;
      });

    setClusterGroups(clusterGroups);
  };

  const toggleClusterExpansion = (clusterId: string) => {
    setClusterGroups(prev => 
      prev.map(group => 
        group.cluster_id === clusterId 
          ? { ...group, isExpanded: !group.isExpanded }
          : group
      )
    );
  };

  const getSeverityColor = (score: number): string => {
    if (score >= 8) return 'text-red-600 bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800';
    if (score >= 6) return 'text-orange-600 bg-orange-50 border-orange-200 dark:bg-orange-950/20 dark:border-orange-800';
    if (score >= 4) return 'text-yellow-600 bg-yellow-50 border-yellow-200 dark:bg-yellow-950/20 dark:border-yellow-800';
    return 'text-green-600 bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800';
  };

  const getSeverityLabel = (score: number): string => {
    if (score >= 8) return 'Critical Risk';
    if (score >= 6) return 'High Risk';
    if (score >= 4) return 'Medium Risk';
    if (score >= 2) return 'Low Risk';
    return 'Minimal Risk';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'ongoing': return <Clock className="h-5 w-5 text-blue-600" />;
      case 'failed': return <AlertTriangle className="h-5 w-5 text-red-500" />;
      default: return <Info className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800 pointer-events-none">Completed</Badge>;
      case 'ongoing':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800 pointer-events-none">In Progress</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800 pointer-events-none">Failed</Badge>;
      default:
        return <Badge variant="secondary" className="pointer-events-none">Unknown</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStartAssessment = async () => {
    if (!selectedClusterForAssessment) {
      return;
    }

    // Clear any previous modal error
    setAssessmentModalError('');
    setIsStartingAssessment(true);
    
    try {
      // Use real API to trigger risk assessment
      const request: TriggerRiskAssessmentRequest = {
        cluster_id: selectedClusterForAssessment
      };
      
      await apiClient.triggerRiskAssessment(request);
      
      // Reload assessments to show the new one
      await loadAssessments();
      
      // Clear form and close modal on success
      setSelectedClusterForAssessment('');
      setAssessmentModalError('');
      setShowNewAssessmentDialog(false);
    } catch (err: any) {
      // Extract and clean error message
      let errorMessage = cleanErrorMessage(err) || 'Failed to start risk assessment';
      
      // Handle specific error types
      if (errorMessage.includes('tier limit') || errorMessage.includes('limit reached') || errorMessage.includes('tier allows maximum')) {
        errorMessage = `${errorMessage.trim()}\n\nPlease contact hello@usekestrel.ai to upgrade your plan.`;
      }
      
      // Show error in the modal instead of generic error state
      setAssessmentModalError(errorMessage);
    } finally {
      setIsStartingAssessment(false);
    }
  };

  // Get selected cluster name for display
  const selectedClusterName = useMemo(() => {
    if (selectedClusterId === 'all') return 'All Clusters';
    const cluster = clusters.find(c => c.id === selectedClusterId);
    return cluster ? cluster.cluster_name : 'Unknown Cluster';
  }, [selectedClusterId, clusters]);

  // Get connected clusters for assessment
  const connectedClusters = useMemo(() => {
    return clusters.filter(c => c.is_connected);
  }, [clusters]);

  // Statistics for display
  const stats = useMemo(() => {
    const total = totalCount || assessments.length;
    const ongoing = assessments.filter(a => a.status === 'ongoing').length;
    const completed = assessments.filter(a => a.status === 'completed').length;
    const failed = assessments.filter(a => a.status === 'failed').length;
    
    return { total, ongoing, completed, failed };
  }, [assessments, totalCount]);

  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const renderAssessmentCard = (assessment: RiskAssessment, isGrouped: boolean = false) => {
    const handleAssessmentClick = () => {
      navigate(`/risk-assessment/${assessment.id}`);
    };

    return (
      <Card 
        key={assessment.id} 
        className="hover:shadow-md transition-shadow cursor-pointer"
        onClick={handleAssessmentClick}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            {/* Left side */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                {getStatusIcon(assessment.status)}
                <h3 className="font-medium text-foreground truncate" title={isGrouped ? `Assessment ${assessment.id}` : assessment.cluster_name}>
                  {isGrouped ? `Assessment #${assessment.id.slice(-4)}` : assessment.cluster_name}
                </h3>
                {getStatusBadge(assessment.status)}
              </div>
              
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1" title="Created">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(assessment.created_at)}</span>
                </div>
                {assessment.completed_at && (
                  <div className="flex items-center gap-1" title="Completed">
                    <CheckCircle className="h-4 w-4" />
                    <span>{formatDate(assessment.completed_at)}</span>
                  </div>
                )}
                {assessment.findings_count !== undefined && (
                  <div className="flex items-center gap-1" title="Findings">
                    <Eye className="h-4 w-4" />
                    <span>{assessment.findings_count} findings</span>
                  </div>
                )}
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-6 text-sm">
              {/* Severity Score */}
              {assessment.status === 'completed' && (
                <Tooltip>
                  <TooltipTrigger>
                    <div className="flex items-center gap-2">
                      <div className={cn(
                        "w-3 h-3 rounded-full",
                        assessment.severity_score >= 8 ? "bg-red-500" :
                        assessment.severity_score >= 6 ? "bg-orange-500" :
                        assessment.severity_score >= 4 ? "bg-yellow-500" : "bg-green-500"
                      )} />
                      <Badge className={cn("text-xs border", getSeverityColor(assessment.severity_score))}>
                        {getSeverityLabel(assessment.severity_score)}
                      </Badge>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Severity Score: {assessment.severity_score}/10</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {/* Findings breakdown */}
              {assessment.status === 'completed' && assessment.high_risk_findings !== undefined && (
                <div className="flex items-center gap-2 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <span>{assessment.high_risk_findings}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-orange-500" />
                    <span>{assessment.medium_risk_findings}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-yellow-500" />
                    <span>{assessment.low_risk_findings}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderClusterGroup = (group: ClusterGroup) => (
    <div key={group.cluster_id} className="space-y-3">
      <div className="flex items-center">
        <div className="flex-1 min-w-0">
          <Button
            variant="ghost"
            onClick={() => toggleClusterExpansion(group.cluster_id)}
            className="flex items-center gap-2 p-2 h-auto"
          >
            {group.isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <div className="flex items-center gap-2">
              <KubernetesIcon className="h-4 w-4 text-blue-600" />
              <span className="font-medium text-foreground">{group.cluster_name}</span>
              <Badge variant="secondary" className="text-xs">
                {group.assessments.length} {group.assessments.length === 1 ? 'assessment' : 'assessments'}
              </Badge>
              
              {/* Status counts */}
              <div className="flex items-center gap-2 ml-2">
                {group.ongoingCount > 0 && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3 text-blue-600" />
                    <span className="text-xs text-blue-600 font-medium">{group.ongoingCount}</span>
                  </div>
                )}
                {group.completedCount > 0 && (
                  <div className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-green-600" />
                    <span className="text-xs text-green-600 font-medium">{group.completedCount}</span>
                  </div>
                )}
                {group.failedCount > 0 && (
                  <div className="flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 text-red-600" />
                    <span className="text-xs text-red-600 font-medium">{group.failedCount}</span>
                  </div>
                )}
              </div>
            </div>
          </Button>
        </div>
        
        {/* Latest assessment indicator - fixed width for alignment */}
        <div className="w-48 flex justify-start">
          {group.latestAssessment && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Latest:</span>
              {group.latestAssessment.status === 'completed' ? (
                <>
                  <div className={cn(
                    "w-2 h-2 rounded-full",
                    group.latestAssessment.severity_score >= 8 ? "bg-red-500" :
                    group.latestAssessment.severity_score >= 6 ? "bg-orange-500" :
                    group.latestAssessment.severity_score >= 4 ? "bg-yellow-500" : "bg-green-500"
                  )} />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {getSeverityLabel(group.latestAssessment.severity_score)}
                  </span>
                </>
              ) : group.latestAssessment.status === 'ongoing' ? (
                <>
                  <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    In Progress
                  </span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    Failed
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      
      {group.isExpanded && (
        <div className="grid gap-3 pl-6">
          {group.assessments.map(assessment => renderAssessmentCard(assessment, true))}
        </div>
      )}
    </div>
  );

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <X className="h-5 w-5" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Filters and Controls */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            {/* Cluster Selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-foreground">Cluster:</label>
              <Select value={selectedClusterId} onValueChange={handleClusterChange} disabled={clustersLoading}>
                <SelectTrigger className="w-48">
                  <SelectValue>
                    {clustersLoading ? 'Loading...' : selectedClusterName}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clusters</SelectItem>
                  {clusters.map((cluster) => (
                    <SelectItem key={cluster.id} value={cluster.id}>
                      <div className="flex items-center gap-2">
                        <div className={cn(
                          "w-2 h-2 rounded-full",
                          cluster.is_connected ? "bg-green-500" : "bg-gray-400"
                        )} />
                        {cluster.cluster_name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* New Assessment Button and Statistics */}
          <div className="flex items-center gap-4">
            {/* Statistics */}
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <span className="font-medium">{stats.total}</span>
                <span>assessments</span>
              </div>
              <div className="flex items-center gap-1">
                <Activity className="h-4 w-4 text-blue-600" />
                <span className="font-medium">{stats.ongoing}</span>
                <span>ongoing</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="font-medium">{stats.completed}</span>
                <span>completed</span>
              </div>
            </div>
            
            {/* New Assessment Button */}
            <Dialog open={showNewAssessmentDialog} onOpenChange={(open) => {
              setShowNewAssessmentDialog(open);
              if (open) {
                // Clear errors when opening modal
                setAssessmentModalError('');
              } else {
                // Clear selection when closing modal
                setSelectedClusterForAssessment('');
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-green-600 hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-2" />
                  New Assessment
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Start New Risk Assessment</DialogTitle>
                </DialogHeader>
                
                {/* Error Display */}
                {assessmentModalError && (
                  <Alert variant="destructive" className="mb-4">
                    <XCircle className="h-4 w-4" />
                    <AlertDescription className="whitespace-pre-wrap">{assessmentModalError}</AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Select Cluster
                    </label>
                    <Select value={selectedClusterForAssessment} onValueChange={setSelectedClusterForAssessment}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a connected cluster" />
                      </SelectTrigger>
                      <SelectContent>
                        {connectedClusters.map((cluster) => (
                          <SelectItem key={cluster.id} value={cluster.id}>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-green-500" />
                              {cluster.cluster_name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {connectedClusters.length === 0 && (
                      <p className="text-sm text-muted-foreground mt-2">
                        No connected clusters available for assessment.
                      </p>
                    )}
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => {
                      setShowNewAssessmentDialog(false);
                      setAssessmentModalError('');
                      setSelectedClusterForAssessment('');
                    }}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleStartAssessment}
                      disabled={!selectedClusterForAssessment || isStartingAssessment}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {isStartingAssessment ? 'Starting...' : 'Start Assessment'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : assessments.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-2 text-sm font-medium text-foreground">No risk assessments found</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {selectedClusterId === 'all' 
                ? 'No risk assessments have been created yet.'
                : `No assessments found for ${selectedClusterName}.`}
            </p>
            <div className="mt-6">
              <Button 
                onClick={() => setShowNewAssessmentDialog(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Start Your First Assessment
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {selectedClusterId === 'all' ? (
              // Show grouped by cluster for all clusters view
              <div className="space-y-6">
                {clusterGroups.map(renderClusterGroup)}
              </div>
            ) : (
              // Show flat list for specific cluster
              <div className="space-y-4">
                {assessments.map(assessment => renderAssessmentCard(assessment, false))}
              </div>
            )}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}